import {AttrsSchema, Component, ConfigOptions, jsx} from 'panel';

import {defineAndMakeJsxFactory} from 'common/util/jsx';
import {FunnelQueryResponse} from 'common/report/data/funnels/api-types';
import {FunnelBookmark} from 'common/report/models/funnels/types';
import {BarVizEl} from 'common/charts/visualization/bar-viz';
import {SegmentFilterController, SegmentSection} from 'common/charts/contexts/segment-filter-controller';
import {ChartsContextRegistry} from 'common/charts/contexts/registry';
import {
  bookmarkHasTimeComparison,
  BucketValueInfo,
  FunnelSegmentExtras,
  generateLegendSections,
  getBarChartDataFromRawFunnel,
} from 'common/report/util/funnels/bar-chart';
import {Bar, BarClickEvent, BarSegment, SegmentWithoutChildren} from 'common/charts/visualization/bar-viz/types';
import {TooltipSection} from 'common/components/chart-tooltip/types';
import {getToolTipSections} from 'common/report/models/funnels/bar-chart/utils';
import {getToolTipText} from 'common/report/models/funnels/time-to-convert/utils';
import {getCalendarDateLabelsFromDates} from 'common/report/models/funnels/utils';
import {DateRange} from 'common/widgets/date-range-picker/models/date-range';
import {formatDateString} from 'common/report/util/funnels/utils';
import {DEFAULT_SELECTED_SEGMENTS_LIMIT, MAX_SELECTED_SEGMENTS_LIMIT, ReportName} from 'common/report/constants';

import css from './index.styl?inline';

interface TimeToConvertDistributionChartAttrs {
  'project-as-of-date': string;
}

// TODO(sam) support any order steps and grouped steps
export class FunnelsTimeToConvertDistributionChart extends Component<
  EmptyObj,
  TimeToConvertDistributionChartAttrs,
  any,
  any,
  ChartsContextRegistry
> {
  private _funnelsResponse: FunnelQueryResponse;
  private _bookmark: FunnelBookmark;

  static get attrsSchema(): AttrsSchema<TimeToConvertDistributionChartAttrs> {
    return {
      ...super.attrsSchema,
      'project-as-of-date': `string`,
    };
  }

  connectedCallback() {
    // Have to call processData after creation of the element in the DOM tree (so the context can be grabbed from parent),
    // but before first render
    this.processData();

    super.connectedCallback();
  }

  private get segmentFilterController(): SegmentFilterController {
    return this.getContext(`segmentFilterController`);
  }

  get config(): ConfigOptions<EmptyObj, any, ChartsContextRegistry> {
    return {
      useShadowDom: true,
      css,
      contexts: [`segmentFilterController` as const],
      defaultContexts: {
        segmentFilterController: new SegmentFilterController({
          defaultNumSelections: DEFAULT_SELECTED_SEGMENTS_LIMIT[ReportName.Funnels],
          selectAllLimit: MAX_SELECTED_SEGMENTS_LIMIT[ReportName.Funnels],
        }),
      },
      template: () => this.renderTemplate(),
    };
  }

  public set funnelsResponse(response: FunnelQueryResponse) {
    this._funnelsResponse = response;

    this.processData();
  }

  public set bookmark(bookmark: FunnelBookmark) {
    this._bookmark = bookmark;

    this.processData();
  }

  private processData() {
    if (!(this._funnelsResponse && this._bookmark && this.isConnected)) {
      return;
    }

    const {sections, savedLegend} = generateLegendSections(this._funnelsResponse, this._bookmark);

    this.segmentFilterController.loadSections(sections);
    this.segmentFilterController.loadFromSavedLegend(savedLegend);

    this.update();
  }

  private renderTemplate() {
    const selectedSections = this.segmentFilterController
      .getSections()
      .map((segmentSection: SegmentSection, i) => ({
        ...segmentSection,
        items: segmentSection.items
          .filter((v) => this.segmentFilterController.isItemSelected(i, v.id))
          .map((v) => ({...v, color: this.segmentFilterController.getItemColor(i, v.id)})),
      }))
      // Reverse is because we want the deepest segmentation to be the first in the list
      .reverse();

    const segments = getBarChartDataFromRawFunnel({
      funnelsResponse: this._funnelsResponse,
      selectedSections,
      bookmark: this._bookmark,
    });

    return (
      <div sel='.ttc-distribution-widget'>
        <BarVizEl
          attrs={{
            'enable-bar-click-events': true,
          }}
          props={{
            chartData: segments,
            tooltipDataCallback: this.createTooltipDataCallback(),
          }}
          on={{
            barClick: (ev: CustomEvent<BarClickEvent<FunnelSegmentExtras & BucketValueInfo>>) => {
              const {x, y} = this.getBoundingClientRect();
              const {segment, barSegment, pageX, pageY} = ev.detail;
              const segmentPath = segment.extras.segmentPath;
              if (barSegment.extras.isOutsideBound) {
                return;
              }

              const gap = 16;
              const left = pageX - x - window.scrollX + gap;
              const top = pageY - y - window.scrollY + gap;

              // n.b. we have to do a little bit of work to match what funnels usually receives from the old bar chart.
              // so we construct what it's expecting for the name/label (of a flattened segment), and match the shape
              // that it expects.
              const selection = {
                queryIndex: barSegment.extras.bucketIdx,
                segment: {
                  name: segmentPath.map((segment) => segment.id).join(` / `),
                  label: segmentPath.map((segment) => segment.label).join(` / `),
                  count: barSegment.extras.count,
                },
                rowLabel: segment.label,
                clientX: left,
                clientY: top,
              };
              this.dispatchEvent(
                new CustomEvent(`openPopover`, {
                  detail: {
                    selection,
                  },
                }),
              );
            },
          }}
        />
      </div>
    );
  }

  createTooltipDataCallback() {
    return (segment: SegmentWithoutChildren, _bar: Bar, barSegment: BarSegment<BucketValueInfo>): TooltipSection[] => {
      const dataBar = barSegment;

      let compareBar;
      let compareSections;
      const hasCompareData = bookmarkHasTimeComparison(this._bookmark);
      if (hasCompareData) {
        compareBar = segment.bars[1].segments[0];
        compareSections = getToolTipText(compareBar.extras.count, compareBar.extras.percentage, segment.label);
      }

      const sections = getToolTipText(dataBar.extras.count, dataBar.extras.percentage, segment.label);

      return getToolTipSections(
        segment.extras.parentLabel,
        barSegment.color,
        sections,
        compareSections,
        getCalendarDateLabelsFromDates(
          DateRange.fromBookmarkFormat(this._bookmark.date_range),
          hasCompareData ? new Date(formatDateString(Object.keys(this._funnelsResponse.compare_data)[0])) : null,
          this.attr(`project-as-of-date`),
        ),
      );
    };
  }
}

export const FunnelsTimeToConvertDistributionChartWidgetEl = defineAndMakeJsxFactory(
  `mp-funnels-ttc-distribution-chart-widget`,
  FunnelsTimeToConvertDistributionChart,
);
